import { PageHeader , Card, CardContent } from "@roberttamas/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { SiteCreateWizard } from "@/components/site-create-wizard";
import { type SiteFormValues } from "@/components/site-form";
import { createSite, listTemplates } from "@/lib/api";
import { buildColorScheme, DEFAULT_SEED, DEFAULT_SEEDS } from "@/lib/color-scheme";
import type { AppError } from "@/lib/errors";
export function NewSitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof SiteFormValues, string>>>(
    {},
  );

  // New sites start from the operator-chosen default template (if any),
  // copying its colors + fonts and recording its id.
  const templatesQuery = useQuery({
    queryKey: ["templates"],
    queryFn: listTemplates,
  });
  const templatesData = templatesQuery.data;
  const defaultId = templatesData?.defaultTemplateId;
  const defaultTemplate = defaultId
    ? templatesData?.templates.find((t) => t.id === defaultId)
    : undefined;

  const mutation = useMutation({
    mutationFn: createSite,
    onSuccess: async (resp) => {
      toast.success(t("siteWizard.created", { slug: resp.site.slug }));
      await queryClient.invalidateQueries({ queryKey: ["sites"] });
      navigate(`/sites/${resp.siteId}`);
    },
    onError: (err: AppError) => {
      // Slug failures (already taken) get mapped to the
      // slug input so the form stays mounted with the rest of the values intact.
      if (err.isSlugError) {
        setFieldErrors({ slug: err.message });
        return;
      }
      toast.error(err.message);
    },
  });

  const handleSubmit = async (values: SiteFormValues) => {
    setFieldErrors({});
    // With a default template, copy its palette + fonts and record its id so
    // the theme picker shows it as active instead of "Custom". Otherwise fall
    // back to the built-in seed palette (the site reads as "Custom").
    const theme = defaultTemplate
      ? {
          templateId: defaultTemplate.id,
          colorScheme: {
            seeds: { seed: defaultTemplate.palette.primary ?? DEFAULT_SEED },
            palette: defaultTemplate.palette,
          },
          fontScheme: defaultTemplate.fontScheme,
        }
      : { colorScheme: buildColorScheme(DEFAULT_SEEDS) };
    // Errors are handled in the mutation's `onError` (slug failures go onto
    // the slug input, anything else toasts). We catch here purely to stop the
    // rejection from bubbling past react-hook-form into the dev overlay.
    try {
      await mutation.mutateAsync({
        name: values.name,
        displayName: values.displayName || undefined,
        slug: values.slug,
        language: values.language,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
        ...theme,
      });
    } catch {
      /* already surfaced via onError */
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader
        eyebrow={t("sites.title")}
        backTo="/sites"
        backLabel={t("sites.backToSites")}
        title={t("sites.newSite")}
        subtitle={t("siteWizard.newSubtitle")}
      />

      <Card>
        <CardContent className="pt-6">
          <SiteCreateWizard
            onSubmit={handleSubmit}
            submitting={mutation.isPending}
            serverFieldErrors={fieldErrors}
          />
        </CardContent>
      </Card>
    </div>
  );
}
